import moment from "moment";

import { getCIPSequenceTextFromCIPSequenceID } from "../pages/defaultCIPInformation/cipSequence.utils";
import { isNullOrUndefined } from "./appUtils";
import {
  createCleaningEffectivenessCell,
  formatCleaningEffectivenessValue,
} from "./cleaningEffectivenessValue";

import { EVENT_TYPE_FORMATTER_STRINGS } from "../constants/global.constants.js";

export const pressureFormatter = (params) => {
  return formatCleaningEffectivenessValue(params.value);
};
export const multipleReasonFormatter = (type) => {
  if (typeof type === "string") {
    type = type.split(",");
  }
  if (Array.isArray(type)) {
    const formattedType = type.map((item) => {
      const trimmedItem = item.trim();
      return trimmedItem;
    });
    const resultString = formattedType.join(", ");
    return resultString;
  }
  return type;
};

export const dateFormatter = (params) => {
  return params.value ? moment(params.value).format("DD/MM/YYYY") : "-";
};

export const typeFormatter = (params, t) => {
  if (!params.value) {
    return "";
  }

  const lowerValue = params.value.toLowerCase();

  switch (lowerValue) {
    case EVENT_TYPE_FORMATTER_STRINGS.C:
    case EVENT_TYPE_FORMATTER_STRINGS.CIP:
      return t("foulingStrings.cip");
    case EVENT_TYPE_FORMATTER_STRINGS.S:
    case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN:
      return t("foulingStrings.shutdown");
    case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN_AS_PREDICTED:
      return t("foulingStrings.shutdownAsPredicted");
    case EVENT_TYPE_FORMATTER_STRINGS.SHUTDOWN_WITH_CIP:
      return t("foulingStrings.shutdownWithCip");
    default:
      return "";
  }
};

export const sourceFormatter = (params, t) => {
  return params.data.is_tool_identified === true
    ? t("IdentifiedCIPStrings.calculation")
    : t("IdentifiedCIPStrings.user");
};

function splitLastOccurrence(str, substring) {
  if (str) {
    const lastIndex = str.lastIndexOf(substring);
    const before = str.slice(0, lastIndex);
    return before;
  } else {
    return str;
  }
}
export const timeFormatter = (params) => {
  if (!params.value) return "-";
  const utcDate = new Date(params.value);
  // Get IST offset in minutes (5 hours 30 minutes = 330 minutes)
  const istOffset = 330;
  const istDate = new Date(utcDate.getTime() + istOffset * 60000);
  // Format as needed, e.g., YYYY-MM-DD HH:mm:ss
  return istDate.toLocaleString("en-IN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZone: "Asia/Kolkata",
  });
};

export const dateComparator = (date1, date2) => {
  const date1Updated = splitLastOccurrence(date1, " ");
  const date2Updated = splitLastOccurrence(date2, " ");
  const d1 = new Date(date1Updated);
  const d2 = new Date(date2Updated);
  if (d1 > d2) {
    return -1;
  } else if (d1 < d2) {
    return 1;
  } else {
    return 0;
  }
};

export const getSortedData = (unsortedData) => {
  const data = [...unsortedData].sort((a, b) => {
    if (!a.start_date && !b.start_date) {
      return 0;
    } else if (!a.start_date) {
      return 1;
    } else if (!b.start_date) {
      return -1;
    } else {
      return new Date(a.start_date) - new Date(b.start_date);
    }
  });

  return data;
};

export const twoDecimalFormatter = (params) => {
  if (params.value == null) {
    return "-";
  }
  return parseFloat(params.value).toFixed(2);
};

export const formatCipSequenceValue = (cipSequenceValue) => {
  if (!cipSequenceValue) return "-";
  const cipSequenceText = getCIPSequenceTextFromCIPSequenceID(cipSequenceValue);
  return cipSequenceText ?? "-";
};

// Format numeric values with validation and optional flag string (W, R, E or combinations)
// @param {number|string} value - The numeric value to format
// @param {string} flagStr - Optional flag string ('W', 'R', 'E', 'RW', etc.)
// @returns {string} Formatted value with bounds checking (-/*/> 100/< 0) or decimal value
export const formatNumericValue = (value, flagStr = null) => {
  if (isNullOrUndefined(value) || value === "-") return "-";

  if (
    typeof value === "object" &&
    !isNullOrUndefined(value) &&
    "value" in value
  ) {
    if (isNullOrUndefined(value.value) || value.value === "-") {
      return "-";
    }
  }

  const inputValue =
    typeof value === "object" && !isNullOrUndefined(value) && "value" in value
      ? value
      : createCleaningEffectivenessCell(value, flagStr);

  return formatCleaningEffectivenessValue(inputValue);
};

export const calculateTableHeight = (rowData = [], maxHeight = 300) => {
  const minEmptyTableHeight = 250;

  if (!rowData?.length) {
    return `${minEmptyTableHeight}px`;
  }

  const rowCount = rowData.length;
  const headerHeight = 80;
  const rowHeight = 42;
  const bufferHeight = 5;

  const calculatedHeight = headerHeight + rowCount * rowHeight + bufferHeight;
  return `${Math.min(calculatedHeight, maxHeight)}px`;
};
